import { useEffect, useState } from "react";
import { OwcIconButton, OwcTypography, OwcProgressSpinner } from "@one/react";
// eslint-disable-next-line import/no-unresolved
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import RichDescriptionEditor from "./RichDescriptionEditor";
import RichDescriptionRenderer from "./RichDescriptionRenderer";
import {
  deriveShortDescription,
  isRichDescriptionEmpty,
  MAX_RICH_DESCRIPTION_LENGTH,
  MAX_SHORT_DESCRIPTION_LENGTH,
} from "./deriveShortDescription";
import { DURATION } from "../../../constants";

const InlineRichDescriptionField = ({
  label = "Description",
  value,
  allowEdit = true,
  disabled = false,
  required = false,
  placeholder,
  onSave,
  onEditStart,
  onEditEnd,
  emptyText = "-",
  testId = "inline-rich-description",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isEditing) setDraft(value || "");
  }, [value, isEditing]);

  const canEdit = allowEdit && !disabled;
  const hasChanges = (draft || "") !== (value || "");
  const isOverLimit = (draft || "").length > MAX_RICH_DESCRIPTION_LENGTH;
  const isRequiredEmpty = required && isRichDescriptionEmpty(draft);
  const requiredErrorText = isRequiredEmpty ? `${label} is required` : null;

  const reset = () => {
    setDraft(value || "");
    setError(null);
    setIsEditing(false);
    setSaving(false);
  };

  const handleEdit = () => {
    if (!canEdit) return;
    setDraft(value || "");
    setError(null);
    setIsEditing(true);
    if (onEditStart) onEditStart();
  };

  const handleCancel = () => {
    reset();
    if (onEditEnd) onEditEnd();
  };

  const handleSave = async () => {
    if (isOverLimit) {
      setError(`Description must be at most ${MAX_RICH_DESCRIPTION_LENGTH} characters`);
      return;
    }
    if (isRequiredEmpty) {
      setError(requiredErrorText);
      return;
    }
    if (!hasChanges) {
      reset();
      if (onEditEnd) onEditEnd();
      return;
    }
    if (!onSave) {
      reset();
      if (onEditEnd) onEditEnd();
      return;
    }
    try {
      setSaving(true);
      const richDescription = draft || "";
      const description = deriveShortDescription(richDescription, MAX_SHORT_DESCRIPTION_LENGTH);
      await onSave({ richDescription, description });
      reset();
      if (onEditEnd) onEditEnd();
    } catch (err) {
      setSaving(false);
      const message = err instanceof Error ? err.message : "Failed to save description";
      try {
        snackbarService.show({ message, type: "error", duration: DURATION });
      } catch (_) { /* ignore */ }
      setError(message);
      reset();
      if (onEditEnd) onEditEnd();
    }
  };

  return (
    <div className="inline-editable-field-container" data-testid={testId}>
      <div className="inline-editable-field-header-row">
        <OwcTypography variant="caption" className="inline-editable-field-label">
          {label}
          {required && (
            <span aria-hidden="true" style={{ marginLeft: "2px" }}>*</span>
          )}
        </OwcTypography>
        {canEdit && !isEditing && (
          <div className="inline-editable-field-actions">
            <OwcIconButton
              icon="edit"
              type="outlined"
              flat
              onClick={(e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                handleEdit();
              }}
              style={{ color: "#0B41CD" }}
              data-testid={`${testId}-edit`}
            />
          </div>
        )}
        {canEdit && isEditing && (
          <div className="inline-editable-field-actions">
            {saving ? (
              <OwcProgressSpinner style={{ width: "24px", height: "24px" }} />
            ) : (
              <>
                <OwcIconButton
                  icon="circle_confirm"
                  type="filled"
                  flat
                  disabled={disabled || saving || !hasChanges || isOverLimit || isRequiredEmpty}
                  onClick={(e) => {
                    if (e && e.stopPropagation) e.stopPropagation();
                    handleSave();
                  }}
                  style={{ color: hasChanges && !isOverLimit && !isRequiredEmpty ? "#28A745" : "#999999" }}
                  data-testid={`${testId}-save`}
                />
                <OwcIconButton
                  icon="circle_clear"
                  type="filled"
                  flat
                  disabled={disabled || saving}
                  onClick={(e) => {
                    if (e && e.stopPropagation) e.stopPropagation();
                    handleCancel();
                  }}
                  style={{ color: "#DC3545" }}
                  data-testid={`${testId}-cancel`}
                />
              </>
            )}
          </div>
        )}
      </div>

      {isEditing ? (
        <RichDescriptionEditor
          value={draft}
          onChange={(md) => {
            setDraft(md);
            if (error) setError(null);
          }}
          disabled={disabled || saving}
          placeholder={placeholder}
          ariaLabel={label}
          errorText={error || requiredErrorText || undefined}
          maxLength={MAX_RICH_DESCRIPTION_LENGTH}
          testId={`${testId}-editor`}
        />
      ) : isRichDescriptionEmpty(value) ? (
        <OwcTypography variant="subtitle2" className="inline-editable-field-value">
          {emptyText}
        </OwcTypography>
      ) : (
        <RichDescriptionRenderer value={value} emptyText={emptyText} testId={`${testId}-renderer`} />
      )}
    </div>
  );
};

export default InlineRichDescriptionField;
